import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import "./styles/Reports.css";

const backendUrl = "https://sokonishushi.co.tz/api";

export default function Reports() {
    const [user, setUser] = useState(null);
    const [attendance, setAttendance] = useState([]);
    const [filteredAttendance, setFilteredAttendance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedAttendance, setSelectedAttendance] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const token = localStorage.getItem("token");

    useEffect(() => {
        const userData = {
            names: localStorage.getItem("names"),
            role: localStorage.getItem("role"),
            staff_id: localStorage.getItem("staff_id")
        };
        setUser(userData);
        fetchAttendance();
    }, []);

    const fetchAttendance = async () => {
        try {
            const response = await fetch(`${backendUrl}/attendance.php`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setAttendance(data.attendance);
                setFilteredAttendance(data.attendance);
            }
        } catch (error) {
            console.error("Error fetching attendance:", error);
        } finally {
            setLoading(false);
        }
    };

    const filterByDate = () => {
        const filtered = attendance.filter(item => {
            return item.sign_date >= startDate && item.sign_date <= endDate;
        });
        setFilteredAttendance(filtered);
        
        Swal.fire({
            icon: 'success',
            title: 'Filtered!',
            text: `Showing ${filtered.length} records from ${startDate} to ${endDate}`,
            timer: 2000,
            showConfirmButton: false
        });
    };

    const viewDetails = (record) => {
        setSelectedAttendance(record);
        setShowModal(true);
    };

    const downloadPDF = async () => {
        Swal.fire({
            title: 'Generating PDF...',
            text: 'Please wait while we prepare your report.',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            const response = await fetch(`${backendUrl}/download_report.php?start_date=${startDate}&end_date=${endDate}`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `attendance_report_${startDate}_to_${endDate}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            
            Swal.fire({
                icon: 'success',
                title: 'Download Started!',
                text: 'Your report is being downloaded.',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to generate report. Please try again.',
                confirmButtonColor: '#d33'
            });
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = "/";
    };

    // Helper function to format location coordinates safely
    const formatLocation = (latitude, longitude) => {
        if (!latitude || !longitude) return 'N/A';
        
        // Convert to numbers if they're strings
        const lat = typeof latitude === 'string' ? parseFloat(latitude) : latitude;
        const lng = typeof longitude === 'string' ? parseFloat(longitude) : longitude;
        
        // Check if conversion was successful and values are valid numbers
        if (isNaN(lat) || isNaN(lng)) return 'Invalid coordinates';
        
        // Format with 4 decimal places
        return `📍 ${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    };

    // Helper function to get Google Maps link
    const getMapLink = (latitude, longitude) => {
        if (!latitude || !longitude) return null;
        
        const lat = typeof latitude === 'string' ? parseFloat(latitude) : latitude;
        const lng = typeof longitude === 'string' ? parseFloat(longitude) : longitude;
        
        if (isNaN(lat) || isNaN(lng)) return null;
        
        return `https://www.google.com/maps?q=${lat},${lng}`;
    };

    if (loading) {
        return (
            <div className="reports-loading">
                <div className="spinner"></div>
                <p>Loading reports...</p>
            </div>
        );
    }

    return (
        <div className="reports-container">
            {/* Sidebar */}
            <aside className="sidebar">
                <div className="sidebar-header">
                    <h2>Attendance System</h2>
                    <div className="user-info">
                        <div className="user-name">{user?.names || 'Admin'}</div>
                        <div className="user-role admin">Admin</div>
                    </div>
                </div>
                <nav className="sidebar-nav">
                    <Link to="/dashboard" className="nav-link">📊 Overview</Link>
                    <Link to="/attendance" className="nav-link">📍 Attendance</Link>
                    <Link to="/schedule" className="nav-link">📅 Schedule</Link>
                    <Link to="/reports" className="nav-link active">📄 Reports</Link>
                    <button onClick={handleLogout} className="nav-link logout">🚪 Logout</button>
                </nav>
            </aside>

            {/* Main Content */}
            <main className="reports-main">
                <div className="reports-header">
                    <h1>Attendance Reports</h1>
                </div>

                {/* Filter Section */}
                <div className="filter-section">
                    <div className="filter-group">
                        <label>Start Date</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div className="filter-group">
                        <label>End Date</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    <div className="filter-actions">
                        <button className="filter-btn" onClick={filterByDate}>Apply Filter</button>
                        <button className="download-btn" onClick={downloadPDF}>Download PDF</button>
                    </div>
                </div>

                {/* Stats Summary */}
                <div className="stats-summary">
                    <div className="summary-card">
                        <div className="summary-number">{filteredAttendance.length}</div>
                        <div className="summary-label">Total Records</div>
                    </div>
                    <div className="summary-card">
                        <div className="summary-number">
                            {filteredAttendance.filter(a => a.action === 'sign_in').length}
                        </div>
                        <div className="summary-label">Sign Ins</div>
                    </div>
                    <div className="summary-card">
                        <div className="summary-number">
                            {filteredAttendance.filter(a => a.action === 'sign_out').length}
                        </div>
                        <div className="summary-label">Sign Outs</div>
                    </div>
                </div>

                {/* Attendance Table */}
                <div className="table-container">
                    <table className="reports-table">
                        <thead>
                            <tr>
                                <th>Staff Name</th>
                                <th>Phone</th>
                                <th>Action</th>
                                <th>Date</th>
                                <th>Time</th>
                                <th>Location</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAttendance.length > 0 ? (
                                filteredAttendance.map((record) => (
                                    <tr key={record.id}>
                                        <td>{record.staff_name || 'N/A'}</td>
                                        <td>{record.phonenumber || 'N/A'}</td>
                                        <td>
                                            <span className={`action-badge ${record.action}`}>
                                                {record.action === 'sign_in' ? 'Sign In' : 'Sign Out'}
                                            </span>
                                        </td>
                                        <td>{record.sign_date || 'N/A'}</td>
                                        <td>{record.sign_time || 'N/A'}</td>
                                        <td>
                                            {formatLocation(record.latitude, record.longitude)}
                                        </td>
                                        <td>
                                            <button className="view-btn" onClick={() => viewDetails(record)}>
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="empty-message">No attendance records found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </main>

            {/* Modal for Viewing Details */}
            {showModal && selectedAttendance && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Attendance Details</h2>
                            <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-row">
                                <strong>Staff Name:</strong> {selectedAttendance.staff_name || 'N/A'}
                            </div>
                            <div className="detail-row">
                                <strong>Phone Number:</strong> {selectedAttendance.phonenumber || 'N/A'}
                            </div>
                            <div className="detail-row">
                                <strong>Action:</strong> 
                                <span className={`action-badge ${selectedAttendance.action}`}>
                                    {selectedAttendance.action === 'sign_in' ? 'Sign In' : 'Sign Out'}
                                </span>
                            </div>
                            <div className="detail-row">
                                <strong>Date:</strong> {selectedAttendance.sign_date || 'N/A'}
                            </div>
                            <div className="detail-row">
                                <strong>Time:</strong> {selectedAttendance.sign_time || 'N/A'}
                            </div>
                            <div className="detail-row">
                                <strong>Location:</strong>
                                {(() => {
                                    const mapLink = getMapLink(selectedAttendance.latitude, selectedAttendance.longitude);
                                    return mapLink ? (
                                        <a 
                                            href={mapLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="map-link"
                                        >
                                            View on Map
                                        </a>
                                    ) : 'N/A';
                                })()}
                            </div>
                            <div className="detail-row">
                                <strong>Selfie Image:</strong>
                                <div className="selfie-image">
                                    {selectedAttendance.selfie_image ? (
                                        <img 
                                            src={`https://sokonishushi.co.tz/${selectedAttendance.selfie_image}`}
                                            alt="Selfie"
                                            onError={(e) => {
                                                e.target.src = '/placeholder.png';
                                                e.target.onerror = null;
                                            }}
                                        />
                                    ) : (
                                        <p>No selfie available</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}